import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { Me } from '../App';

interface EventRow {
  id: string;
  summary: string;
  startsAt: string;
  endsAt: string;
  location?: string | null;
  attendees: { email: string; rsvp: string }[];
  calendar: { name: string; color: string };
}

export function Dashboard({ me }: { me: Me }) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<EventRow[]>('/api/events').then((data) => {
      setEvents(data);
      setLoading(false);
    });
  }, []);

  const upcoming = events.filter((e) => new Date(e.startsAt) >= new Date()).slice(0, 5);
  const totalAttendees = events.reduce((acc, e) => acc + e.attendees.length, 0);
  const acceptedCount = events.reduce(
    (acc, e) => acc + e.attendees.filter((a) => a.rsvp === 'ACCEPTED').length,
    0,
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="card bg-brand-gradient text-white border-0">
        <div className="text-meta uppercase tracking-[0.2em] opacity-80">Willkommen zurueck</div>
        <h1 className="text-h2 mt-2">{me.name ?? me.email}</h1>
        <div className="text-body opacity-90 mt-2">
          ForgeCalendar laeuft. Termine erstellen, Einladungen versenden, RSVP-Status sichtbar verfolgen.
        </div>
        <div className="grid grid-cols-3 gap-6 mt-6">
          <KpiInline label="Termine" value={events.length} />
          <KpiInline label="Teilnehmer" value={totalAttendees} />
          <KpiInline label="Zugesagt" value={acceptedCount} />
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-h4">Naechste Termine</h2>
          <Link to="/events" className="btn btn-secondary text-small">Alle anzeigen</Link>
        </div>
        {loading ? (
          <div className="text-ink-secondary">Lade …</div>
        ) : upcoming.length === 0 ? (
          <div className="text-ink-secondary text-small">Keine kommenden Termine.</div>
        ) : (
          <ul className="divide-y divide-border/60">
            {upcoming.map((e) => (
              <li key={e.id} className="py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: e.calendar.color }} />
                  <div className="min-w-0">
                    <Link to={`/events/${e.id}`} className="text-body font-medium hover:underline truncate block">
                      {e.summary}
                    </Link>
                    <div className="text-small text-ink-muted">
                      {new Date(e.startsAt).toLocaleString('de-DE')} – {e.calendar.name}
                      {e.location ? ` · ${e.location}` : ''}
                    </div>
                  </div>
                </div>
                <div className="text-meta text-ink-muted">{e.attendees.length} Teilnehmer</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function KpiInline({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-meta uppercase tracking-[0.2em] opacity-80">{label}</div>
      <div className="text-h2">{value}</div>
    </div>
  );
}
